import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { requestServise } from "@/shared";
import {
  IMailingSliceStateWithSubs,
  IUserMailingSetting,
  SubscriptionKey,
} from "./type";
import {
  addUserToSub,
  removeUserFromSub,
  parseDealersToSubscriptions,
  syncUserInSubscriptions,
} from "./helpers";

/* ---------- async thunks ---------- */
export const getMailingUsersList = requestServise.get<
  { users: IUserMailingSetting[] },
  string
>(
  "mailing/getMailingUsersList",
  (id: string) => `notifications/all-with-details?dealerId=${id}`,
);

export const getUserMailingSettings = requestServise.get<
  IUserMailingSetting,
  string
>(
  "mailing/getUserMailingSettings",
  (id: string) => `notifications/user/${id}/subscriptions`,
);

export const changeUserMailingSettings = requestServise.put<
  IUserMailingSetting,
  IUserMailingSetting
>(
  "mailing/changeUserMailingSettings",
  (user) => `notifications/user/${user.userId}`,
);

export const saveMailingSettings = requestServise.patch<
  { success: boolean },
  { users: IUserMailingSetting[] }
>("mailing/saveMailingSettings", "notifications/batch-update");

/* ---------- initial state ---------- */
const initialState: IMailingSliceStateWithSubs = {
  isLoading: false,
  error: null,
  mailingUsersList: [],
  firstMailingUsersList: [],
  currentUser: null,
  subscriptions: {
    pledgeBook: [],
    orderPd: [],
    accountsReceivable: [],
    salesAndRemainsWeekly: [],
    salesAndRemainsMonthly: [],
  },
  firstSubscriptions: {
    pledgeBook: [],
    orderPd: [],
    accountsReceivable: [],
    salesAndRemainsWeekly: [],
    salesAndRemainsMonthly: [],
  },
};

/* ---------- slice ---------- */
export const mailingSettings = createSlice({
  name: "mailing",
  initialState,
  reducers: {
    setCurrentUser(state, action: PayloadAction<IUserMailingSetting | null>) {
      state.currentUser = action.payload;
    },

    addUserToSubscription(
      state,
      action: PayloadAction<{
        key: SubscriptionKey;
        user: IUserMailingSetting;
      }>,
    ) {
      const { key, user } = action.payload;
      addUserToSub(state.subscriptions, key, user);

      const userSubs = state.mailingUsersList.find(
        (u) => u.userId === user.userId,
      );
      if (userSubs) {
        switch (key) {
          case "pledgeBook":
            userSubs.pledgeBook = true;
            break;
          case "orderPd":
            userSubs.orderPd = true;
            break;
          case "accountsReceivable":
            userSubs.accountsReceivable = true;
            userSubs.overdAccountsReceivable = true;
            break;
          case "salesAndRemainsWeekly":
            userSubs.salesAndRemainsWeekly = true;
            break;
          case "salesAndRemainsMonthly":
            userSubs.salesAndRemainsMonthly = true;
            break;
        }
      }
    },

    removeUserFromSubscription(
      state,
      action: PayloadAction<{ key: SubscriptionKey; userId: string }>,
    ) {
      const { key, userId } = action.payload;
      removeUserFromSub(state.subscriptions, key, userId);

      const userSubs = state.mailingUsersList.find((u) => u.userId === userId);
      if (userSubs) {
        switch (key) {
          case "pledgeBook":
            userSubs.pledgeBook = false;
            break;
          case "orderPd":
            userSubs.orderPd = false;
            break;
          case "accountsReceivable":
            userSubs.accountsReceivable = false;
            userSubs.overdAccountsReceivable = false;
            break;
          case "salesAndRemainsWeekly":
            userSubs.salesAndRemainsWeekly = false;
            break;
          case "salesAndRemainsMonthly":
            userSubs.salesAndRemainsMonthly = false;
            break;
        }
      }
    },
    resetSubscriptions(state) {
      state.subscriptions = state.firstSubscriptions;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getMailingUsersList.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      getMailingUsersList.fulfilled,
      (state, action: PayloadAction<{ users: IUserMailingSetting[] }>) => {
        state.isLoading = false;
        state.error = null;
        state.mailingUsersList = action.payload.users;
        state.firstMailingUsersList = action.payload.users;
        state.subscriptions = parseDealersToSubscriptions(action.payload.users);
        state.firstSubscriptions = parseDealersToSubscriptions(
          action.payload.users,
        );
      },
    );
    builder.addCase(getMailingUsersList.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    builder.addCase(getUserMailingSettings.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      getUserMailingSettings.fulfilled,
      (state, action: PayloadAction<IUserMailingSetting>) => {
        state.isLoading = false;
        state.error = null;
        state.currentUser = action.payload;
      },
    );
    builder.addCase(getUserMailingSettings.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    builder.addCase(changeUserMailingSettings.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      changeUserMailingSettings.fulfilled,
      (state, action: PayloadAction<IUserMailingSetting>) => {
        state.isLoading = false;
        state.error = null;

        const updated = action.payload;
        const idx = state.mailingUsersList.findIndex(
          (u) => u.userId === updated.userId,
        );
        if (idx >= 0) {
          state.mailingUsersList[idx] = updated;
        } else {
          state.mailingUsersList.push(updated);
        }

        syncUserInSubscriptions(state.subscriptions, updated);

        if (state.currentUser?.userId === updated.userId) {
          state.currentUser = updated;
        }
      },
    );
    builder.addCase(changeUserMailingSettings.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    builder.addCase(saveMailingSettings.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(saveMailingSettings.fulfilled, (state) => {
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(saveMailingSettings.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload ?? "Ошибка сохранения";
    });
  },
});

export const {
  setCurrentUser,
  addUserToSubscription,
  removeUserFromSubscription,
  resetSubscriptions,
} = mailingSettings.actions;

export default mailingSettings.reducer;
